/*
 * Access audit — filtered slice of the audit log limited to
 * authentication / authorization events. Same backing endpoint as the
 * full Audit page; the focus here is "who logged in / failed / had
 * their role changed".
 */

import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { api, ApiError } from "../lib/api.js";
import { SkeletonTable } from "../components/Skeleton.js";

type Event = { id: string; action: string; actorRole: string; actorUserId?: string; occurredAt: string; details: Record<string, unknown> };

const AUTH_KEYWORDS = ["auth", "login", "logout", "role", "permission", "session", "mfa", "secret"];

function isAuthEvent(action: string): boolean {
  const a = action.toLowerCase();
  return AUTH_KEYWORDS.some((k) => a.includes(k));
}

export default function AccessAudit() {
  const [events, setEvents] = useState<Event[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.audit()
      .then((r) => {
        const list = Array.isArray(r?.events) ? r.events : [];
        setEvents(list.filter((e) => isAuthEvent(e.action)));
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, []);

  const rows = useMemo(() => {
    if (!Array.isArray(events)) return [];
    if (!search.trim()) return events;
    const q = search.toLowerCase();
    return events.filter((e) => [e.action, e.actorRole, e.actorUserId].filter(Boolean).some((f) => String(f).toLowerCase().includes(q)));
  }, [events, search]);

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Access audit</h1>
          <p className="muted">
            Authentication, authorization, role-change, and secret-rotation events. Sourced from the platform audit log.
          </p>
        </div>
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}

      <section className="transfer-filters">
        <label className="transfer-filter transfer-filter-grow">
          Search
          <input type="search" placeholder="action / actor…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
      </section>

      {events === null && <SkeletonTable rows={5} columns={4} />}
      {events !== null && rows.length === 0 && (
        <div className="empty-state-card">
          <h2 style={{ marginTop: 0 }}>No access events match</h2>
          <p>Either nothing's happened recently or your filter excludes it. Drop the search to see all auth events.</p>
        </div>
      )}

      {rows.length > 0 && (
        <table className="transfer-table">
          <thead>
            <tr>
              <th>When</th>
              <th>Action</th>
              <th>Actor</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((e) => (
              <tr key={e.id}>
                <td className="muted" style={{ fontSize: 12 }}>{e.occurredAt}</td>
                <td className="mono" style={{ fontSize: 12 }}>{e.action}</td>
                <td><span className="pill pill-muted">{e.actorRole}</span> {e.actorUserId && <code style={{ fontSize: 11 }}>{e.actorUserId}</code>}</td>
                <td style={{ fontSize: 11, color: "var(--text-muted)", maxWidth: 480 }}>
                  {Object.keys(e.details ?? {}).length > 0 ? JSON.stringify(e.details).slice(0, 160) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
